import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, CardMedia, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';

const FeedContainer = styled(Box)(({ theme }) => ({
  padding: '6rem 0',
  backgroundColor: '#f8fafc',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '1rem',
  color: '#1e293b',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem'
  }
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  textAlign: 'center',
  marginBottom: '4rem',
  color: '#64748b',
  maxWidth: '600px',
  margin: '0 auto 4rem auto'
}));

const FeedCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '24px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
  }
}));

const FeedImage = styled(CardMedia)(({ theme }) => ({
  height: 200,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative'
}));

const FeedContent = styled(CardContent)(({ theme }) => ({
  padding: '2rem'
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#3b82f6',
  color: 'white',
  fontWeight: 600,
  marginBottom: '1rem',
  '& .MuiChip-label': {
    fontSize: '0.875rem'
  }
}));

const FeedTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: '1rem',
  color: '#1e293b',
  lineHeight: 1.4
}));

const FeedExcerpt = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#64748b',
  lineHeight: 1.6,
  marginBottom: '1rem'
}));

const FeedDate = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#94a3b8',
  fontWeight: 500
}));

const FeedSection = ({ feedPosts }) => {
  const defaultFeedPosts = [
    {
      title: "New Product Launch Success",
      excerpt: "We are excited to announce the successful launch of our latest product line, featuring innovative solutions...",
      image: "https://c.animaapp.com/mffcpu90bnu9dd/img/ai_4.png",
      date: "2024-01-15",
      category: "Product News"
    },
    {
      title: "Team Expansion Update", 
      excerpt: "Our team continues to grow with talented professionals joining our mission to deliver exceptional services...",
      image: "https://c.animaapp.com/mffcpu90bnu9dd/img/ai_3.png",
      date: "2024-01-10",
      category: "Company News"
    },
    {
      title: "Industry Recognition Award",
      excerpt: "We are honored to receive recognition for our innovative approach and commitment to excellence in the industry...",
      image: "https://c.animaapp.com/mffcpu90bnu9dd/img/ai_1.png",
      date: "2024-01-05", 
      category: "Awards"
    }
  ];

  const displayFeedPosts = feedPosts || defaultFeedPosts;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <FeedContainer>
      <Container maxWidth="lg">
        <SectionTitle variant="h2">
          Latest News & Updates
        </SectionTitle>
        <SectionSubtitle variant="body1">
          Stay updated with our latest news, product launches, and company milestones
        </SectionSubtitle>
        <Grid container spacing={4}>
          {displayFeedPosts.map((post, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeedCard>
                <FeedImage
                  image={post.image}
                  title={post.title}
                />
                <FeedContent>
                  <CategoryChip 
                    label={post.category} 
                    size="small"
                  />
                  <FeedTitle variant="h6">
                    {post.title}
                  </FeedTitle>
                  <FeedExcerpt variant="body2">
                    {post.excerpt}
                  </FeedExcerpt>
                  <FeedDate variant="caption">
                    {formatDate(post.date)}
                  </FeedDate>
                </FeedContent>
              </FeedCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </FeedContainer>
  );
};

export default FeedSection;
